import { Stack, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { Circle, CircleCheck } from "lucide-react";
import { type FC, useRef } from "react";
import type { GetTaskWorkflowStepResponse } from "web_myplann/Infrastructure/Api/Api";
import { ExpandToggleButton } from "web_myplann/Shared/Components/ExpandToggleButton";
import { useTextTruncation } from "web_myplann/Shared/Hooks/useTextTruncation";

type Props = {
	last: boolean;
	first: boolean;
	data: GetTaskWorkflowStepResponse;
};

export const Step: FC<Props> = ({ last, first, data }) => {
	const { isCompleted, name, dueDate, completedDate } = data;
	const theme = useTheme();
	const textRef = useRef<HTMLSpanElement>(null);
	const { isTruncated, isExpanded, toggleExpanded } = useTextTruncation(
		textRef,
		[name],
	);

	const Icon = isCompleted ? CircleCheck : Circle;
	const formattedDate = isCompleted
		? completedDate
			? dayjs(completedDate).format("D.M.YYYY")
			: null
		: dueDate
			? dayjs(dueDate).format("D.M.YYYY")
			: null;

	return (
		<Stack direction="row" gap={0.8} minHeight="45px">
			<Stack direction="column" alignItems="center" width={24}>
				{first ? (
					<Stack flex={1} marginBottom="8px" />
				) : (
					<Stack
						border={`1px solid ${theme.palette.grey[300]}`}
						flex={1}
						width="0px"
						marginBottom="5px"
					/>
				)}

				<Icon
					color={
						isCompleted ? theme.palette.primary.main : theme.palette.grey[300]
					}
					strokeWidth={1.5}
					style={{ pointerEvents: "none" }}
				/>

				{last ? (
					<Stack flex={1} marginTop="8px" />
				) : (
					<Stack
						border={`1px solid ${theme.palette.grey[300]}`}
						flex={1}
						width="0px"
						marginTop="5px"
					/>
				)}
			</Stack>

			<Stack
				display="flex"
				flexDirection="column"
				justifyContent="center"
				flex={1}
				minWidth={0}
				paddingY={0.5}
			>
				<Typography
					ref={textRef}
					fontSize={14}
					fontWeight={600}
					lineHeight="24px"
					color={
						isCompleted ? theme.palette.grey[500] : theme.palette.grey[700]
					}
					sx={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						...(!isExpanded && {
							display: "-webkit-box",
							WebkitLineClamp: 2,
							WebkitBoxOrient: "vertical",
						}),
					}}
				>
					{name}
				</Typography>
				{(isTruncated || isExpanded) && (
					<ExpandToggleButton
						isExpanded={isExpanded}
						onClick={toggleExpanded}
						fontSize={12}
					/>
				)}
				{formattedDate && (
					<Typography
						fontSize={12}
						fontWeight={400}
						color={theme.palette.grey[500]}
					>
						{formattedDate}
					</Typography>
				)}
			</Stack>
		</Stack>
	);
};
